// Custom hook for managing mission form state and validation
// Handles form state, validation, and change detection for MissionModal
// CREATED: Unified hook for both creation and modification modes

import { useState, useEffect } from 'react';
import type { TaskItem } from '../lib/supabase';
import type { TemplateWithItems } from '../lib/templates';
import type { RecurrenceConfig } from '../components/templates/RecurrenceConfigForm';

interface UseMissionFormProps {
  template?: TemplateWithItems | null;
  isEditing: boolean;
}

export function useMissionForm({ template, isEditing }: UseMissionFormProps) {
  // Step 1: General information
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [serviceId, setServiceId] = useState<string>('');

  // Step 2: Tasks
  const [items, setItems] = useState<TaskItem[]>([]);

  // Step 3: Recurrence
  const [isRecurrent, setIsRecurrent] = useState(false);
  const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig>({
    preset: 'daily',
    frequency: 'daily',
    daysOfWeek: [],
    monthlyMode: 'day',
    dayOfMonth: 1,
    weekOfMonth: 1,
    selectedDayForWeek: 1,
    interval: 1,
    monthOfYear: 1,
    yearlyMode: 'day',
    endMode: 'never',
    endDate: null,
    endOccurrences: 10
  });

  // Initialize form with template data if editing
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setDuration(template.estimated_duration_minutes || null);
      setServiceId(template.service_id || '');
    }
  }, [template]);

  // Validation functions
  const validateStep1 = (): { valid: boolean; error: string } => {
    if (!name.trim()) {
      return { valid: false, error: 'Le nom est requis' };
    }

    if (name.trim().length < 3) {
      return { valid: false, error: 'Le nom doit contenir au moins 3 caractères' };
    }

    if (duration === null || duration <= 0) {
      return { valid: false, error: 'La durée est requise' };
    }

    if (duration < 15) {
      return { valid: false, error: 'La durée minimum est de 15 minutes' };
    }

    if (!serviceId) {
      return { valid: false, error: 'Le service est requis' };
    }

    return { valid: true, error: '' };
  };

  const validateStep2 = (): { valid: boolean; error: string } => {
    // Filter out empty tasks (tasks with no title)
    const nonEmptyTasks = items.filter(item => item.title.trim());

    if (nonEmptyTasks.length === 0) {
      return { valid: false, error: 'Ajoutez au moins une tâche' };
    }

    return { valid: true, error: '' };
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    // For editing, check if any field changed
    if (isEditing && template) {
      if (name !== template.name) return true;
      if (description !== (template.description || '')) return true;
      if (duration !== template.estimated_duration_minutes) return true;
      if (serviceId !== (template.service_id || '')) return true;
      // Note: items and recurrence changes are checked separately in the main component
      return false;
    }

    // For creating, check if any field has been filled
    if (name.trim() !== '') return true;
    if (description.trim() !== '') return true;
    if (duration !== null && duration > 0) return true;
    if (serviceId !== '') return true;
    if (items.length > 0) return true;
    if (isRecurrent) return true;

    return false;
  };

  return {
    // Form state
    name,
    setName,
    description,
    setDescription,
    duration,
    setDuration,
    serviceId,
    setServiceId,
    items,
    setItems,
    isRecurrent,
    setIsRecurrent,
    recurrenceConfig,
    setRecurrenceConfig,

    // Validation
    validateStep1,
    validateStep2,
    hasUnsavedChanges,
  };
}
